import {
  hotelAccessCodeCrudConfig,
  type HotelAccessCode,
} from '@/config/objects/hotel-access-codes';
import { CrudListView, type CrudConfig } from '@startupquickstart/frontend';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface HotelAccessCodesListProps {
  /**
   * Optional custom config (with modified endpoints, actions, etc.)
   */
  config?: CrudConfig<HotelAccessCode>;
  /**
   * Optional default where clause for filtering
   */
  defaultWhere?: Record<string, unknown>;
  /**
   * Optional custom onItemClick handler
   */
  onItemClick?: (hotelAccessCode: HotelAccessCode) => void;
  /**
   * Custom class name for the container
   */
  className?: string;
  /**
   * Base URL for navigation, defaults to /hotel-access-codes
   */
  baseUrl?: string;
  /**
   * Breadcrumbs for navigation
   */
  breadcrumbs?: Array<{ label: string; href?: string; current?: boolean }>;
}

/**
 * Reusable hotel access codes list component
 * Pass a custom config with modified actions to customize behavior
 */
export function HotelAccessCodesList({
  config,
  baseUrl = '/hotel-access-codes',
  defaultWhere,
  onItemClick,
  className,
  breadcrumbs,
}: HotelAccessCodesListProps) {
  const navigate = useNavigate();

  const _onItemClick = useCallback(
    (hotelAccessCode: HotelAccessCode) => {
      if (onItemClick) {
        onItemClick(hotelAccessCode);
      } else {
        navigate(`/hotel-access-codes/${hotelAccessCode.id}`);
      }
    },
    [navigate, onItemClick],
  );

  return (
    <div className={className}>
      <CrudListView
        config={config || hotelAccessCodeCrudConfig}
        baseUrl={baseUrl}
        onItemClick={_onItemClick}
        defaultWhere={defaultWhere}
        breadcrumbs={breadcrumbs}
        getValueFromItem={(hotelAccessCode: HotelAccessCode) => hotelAccessCode.id}
        getLabelFromItem={(hotelAccessCode: HotelAccessCode) => hotelAccessCode.name}
      />
    </div>
  );
}
